import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { ClipboardList, Undo2, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { ChangeHistory } from "@/components/coaching/ChangeHistory";
import { toast } from "sonner";

const SOURCE_LABEL = {
  manual: "Manual edit", bulk_action: "Bulk action", applied_scenario: "Applied scenario",
  fixture: "Fixture change", rollback: "Rollback", squad_calendar: "Squad calendar move",
};
const iso = (d) => new Date(d).toISOString().slice(0, 10);

export default function Digest({squad:lockedSquad="",academyId=""}) {
  const [day, setDay] = useState(iso(new Date()));
  const [squad, setSquad] = useState(lockedSquad);
  const [squads, setSquads] = useState([]);
  const [d, setD] = useState(null),[error,setError]=useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/digest?${new URLSearchParams({date_str:day,squad,academy_id:academyId})}`)
      .then((r) => { setD(r.data); setSel([]); setError(""); }).catch(e => {setD(null);setError(typeof e.response?.data?.detail==='string' ? e.response.data.detail : 'Team change digest could not be loaded.');});
  }, [day, squad, academyId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/athletes").then((r) => setSquads(Array.from(new Set(r.data.flatMap((a) => a.teams || [a.squad]))).filter(Boolean))).catch(() => {}); }, []);

  const undo = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(ids.length === 1
      ? "Undo this change? Where it touched the calendar, the exact previous plan is restored."
      : `Undo ${ids.length} changes? Each athlete's calendar goes back exactly as it was.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/digest/undo", { entry_ids: ids });
      const put = data.undone.reduce((n, u) => n + (u.restored_tasks || 0), 0);
      toast.success(`Undid ${data.undone.length} change(s)${put ? ` · ${put} session(s) restored` : ""}`,
        data.failed.length ? { description: `${data.failed.length} could not be undone automatically` } : undefined);
      load();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Undo failed"); }
    finally { setBusy(false); }
  };

  const shift = (n) => setDay(iso(new Date(new Date(day).getTime() + n * 864e5)));
  const allIds = (d?.coaches || []).flatMap((c) => c.entries.filter((e) => e.can_rollback).map((e) => e.entry_id));

  return (
    <div>
      {error && <p data-testid="team-digest-error" role="alert" className="p-5 text-sm">{error}</p>}
      <header className="min-h-16 border-b border-border flex flex-wrap items-center py-3 gap-3 px-4 sm:px-8 sticky top-0 bg-background z-10">
        <ClipboardList className="w-5 h-5" />
        <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">Daily Digest</h1>
        {!lockedSquad && <select data-testid="dg-squad" value={squad} onChange={(e) => setSquad(e.target.value)} className="inp w-auto text-xs py-1.5">
          <option value="">All squads</option>
          {squads.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>}
        <div className="ml-auto flex items-center gap-1">
          <button data-testid="dg-prev" onClick={() => shift(-1)} className="p-1.5 border border-border hover:bg-surface"><ChevronLeft className="w-4 h-4" /></button>
          <input data-testid="dg-date" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="inp w-auto text-xs py-1.5" />
          <button data-testid="dg-next" onClick={() => shift(1)} className="p-1.5 border border-border hover:bg-surface"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </header>

      {lockedSquad && academyId && <div className="px-5 sm:px-8"><ChangeHistory url={`/teams/history?${new URLSearchParams({squad:lockedSquad,academy_id:academyId})}`} title="This team · coaching, calendar and study changes"/></div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 border-b border-border">
        {[["Changes", d?.total_changes], ["Athletes touched", d?.athletes_touched],
          ["Undoable", d?.rollbackable], ["Warnings acknowledged", d?.warnings_acknowledged]].map(([k, v], i) => (
          <div key={k} className={`p-4 sm:p-6 ${i < 3 ? "border-r border-border" : ""} ${i < 2 ? "border-b lg:border-b-0 border-border" : ""}`} data-testid={`dg-kpi-${i}`}>
            <div className="text-[10px] sm:text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground">{k}</div>
            <div className="font-heading text-3xl sm:text-4xl font-black tabular-nums mt-1.5">{v ?? "-"}</div>
          </div>
        ))}
      </div>

      {!d ? (
        <div className="p-8 text-sm text-muted-foreground">Loading digest…</div>
      ) : d.coaches.length === 0 ? (
        <div className="p-8 text-sm text-muted-foreground" data-testid="dg-empty">
          Nothing was changed on {fmtDate(day)}. Pick another day, or come back after your staff have been in.
        </div>
      ) : (
        <>
          <div className="px-4 sm:px-8 py-3 border-b border-border flex flex-wrap gap-2 items-center">
            <button data-testid="dg-select-all" onClick={() => setSel(sel.length === allIds.length ? [] : allIds)}
              className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors">
              {sel.length === allIds.length && allIds.length ? "Clear selection" : `Select all undoable (${allIds.length})`}
            </button>
            <button data-testid="dg-undo-selected" onClick={() => undo(sel)} disabled={busy || !sel.length}
              className="text-xs bg-foreground text-background px-3 py-1.5 disabled:opacity-40 flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Undo selected ({sel.length})
            </button>
          </div>

          {d.coaches.map((c) => (
            <div key={`${c.coach}-${c.role}`} className="border-b border-border" data-testid={`dg-coach-${c.coach}`}>
              <div className="px-4 sm:px-8 py-3 bg-surface flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-heading font-bold tracking-tight">{c.coach}</span>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.role}</span>
                <span className="text-xs text-muted-foreground">{c.changes} change(s) · {c.athletes} athlete(s) · {c.domains.join(", ")}</span>
                {c.warnings > 0 && <span className="text-xs text-traffic-amber flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {c.warnings} warning(s) acknowledged</span>}
              </div>
              <div className="scroll-x">
                <table className="w-full text-sm">
                  <tbody>
                    {c.entries.map((e) => (
                      <tr key={e.entry_id} className={`border-b border-border last:border-0 ${e.rolled_back ? "opacity-50" : ""}`} data-testid={`dg-entry-${e.entry_id}`}>
                        <td className="px-3 py-2 w-8">
                          {e.can_rollback && (
                            <input type="checkbox" data-testid={`dg-pick-${e.entry_id}`} checked={sel.includes(e.entry_id)}
                              onChange={(ev) => setSel((p) => ev.target.checked ? [...p, e.entry_id] : p.filter((x) => x !== e.entry_id))} />
                          )}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground tabular-nums whitespace-nowrap">{fmtDateTime(e.at)}</td>
                        <td className="px-2 py-2 whitespace-nowrap font-medium">{e.athlete_name}</td>
                        <td className="px-2 py-2 text-muted-foreground">{e.domain}</td>
                        <td className="px-2 py-2">
                          {e.field}: <span className="text-muted-foreground">{String(e.old ?? "-")}</span> → <b>{String(e.new ?? "-")}</b>
                          {e.note && <span className="block text-[11px] text-muted-foreground">{e.note}</span>}
                          {(e.warnings || []).length > 0 && (
                            <span className="block text-[11px] text-traffic-amber">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />{e.warnings.map((w) => w.text).join(" · ")}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{SOURCE_LABEL[e.source] || e.source}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {e.can_rollback ? (
                            <button data-testid={`dg-undo-${e.entry_id}`} onClick={() => undo([e.entry_id])} disabled={busy}
                              className="text-xs border border-border px-2 py-1 hover:bg-foreground hover:text-background transition-colors flex items-center gap-1 ml-auto">
                              <Undo2 className="w-3 h-3" /> Undo
                            </button>
                          ) : e.rolled_back ? (
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">rolled back</span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">manual reverse</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
